import Button from '../../components/form-control/Button';
import { Formik } from 'formik';
import Input from '../../components/form-control/Input';
import * as Yup from 'yup';
import { useState } from 'react';
import { ICreateAdminAccountDTO, IEnum } from '../../resources/interfaces';
import { inviteMemberToTeam } from '../../api/requests';
import { SELECTABLE_ACCESS_RIGHTS } from '../../resources/enums';
import {
  getFormattedAccessRight,
  getFormattedPhoneNumber,
  phoneValidationSchema,
  showToast
} from '../../utils/miscFunctions';
import SelectDropdown, {
  ISelectDropdownItem
} from '../../components/form-control/SelectDropdown';
import usePartner from '../../hooks/partnerHook';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getEnumsFromState } from '../../states/slices/resourceSlice';
import useUserAccount from '../../hooks/userAccountHook';

const accessRights: Array<ISelectDropdownItem> = SELECTABLE_ACCESS_RIGHTS.map(
  (value) => ({
    name: getFormattedAccessRight(value),
    value: value
  })
);

const InviteTeamMember = ({
  onProceed,
  onClose,
  team,
  teamID
}: {
  onProceed: () => void;
  onClose: () => void;
  team?: any;
  teamID?: string;
}) => {
  const { user } = useUserAccount();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getAllSlackWorkspaces } = usePartner();
  const enums = useSelector(getEnumsFromState)
  const gender: IEnum[] = enums?.genders
  const { data: workspaces } = useQuery({ queryKey: ['workspaces'], queryFn: getAllSlackWorkspaces });

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload: ICreateAdminAccountDTO = {
      email: values.email.trim(),
      firstName: values.firstName,
      lastName: values.lastName,
      phone: getFormattedPhoneNumber(values.phone),
      accessRights: values.accessRight,
      country: 'Ghana',
      gender: 'UNKNOWN',
      jobTitle: values.jobTitle,
      slackWorkspaceId: values.slackWorkspaceId,
    };
    if (team)
      inviteMemberToTeam(team.id, payload)
        .then((response: any) => {
          if (response) {
            showToast.success(response?.message ?? 'Admin account invite sent successfully', );
            onProceed();
          }
        })
        .catch((error: any) => {
          showToast.error(error?.description);
        })
        .finally(() => setSubmitting(false));
  };

  return (
    <div className="w-full rounded-full">
      <div className="p-5">
        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            jobTitle: '',
            // gender: '',
            accessRight: '',
            slackWorkspaceId: ''
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={Yup.object({
            firstName: Yup.string().trim().required('First name is required.'),
            lastName: Yup.string().trim().required('Last name is required.'),
            email: Yup.string()
              .trim()
              .email('Email provided invalid.')
              .required('Email is required.'),
            phone: phoneValidationSchema(),
            jobTitle: Yup.string().required('Job title is required.'),
            // gender: Yup.string().trim().required('Gender is required'),
            accessRight: Yup.string().trim().required('Access right is required')
          })}
          onSubmit={(values) => {
            const payload = {
              ...values,
              accessRight: values.accessRight
            };
            submitHandler(payload);
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-5">
                <Input
                  name="firstName"
                  label="First name"
                  value={values.firstName}
                  type="text"
                  wrapperClasses=""
                  required
                  error={errors.firstName}
                  onChange={handleChange}
                  onBlur={handleChange}
                  helperText={errors.firstName}
                />

                <Input
                  name="lastName"
                  label="Last name"
                  value={values.lastName}
                  type="text"
                  wrapperClasses=""
                  required
                  error={errors.lastName}
                  onChange={handleChange}
                  onBlur={handleChange}
                  helperText={errors.lastName}
                />

                <Input
                  name="email"
                  label="Email"
                  value={values.email}
                  type="email"
                  wrapperClasses=""
                  required
                  error={errors.lastName}
                  onChange={handleChange}
                  helperText={errors.lastName}
                />

                <Input
                  type={'tel'}
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleChange}
                  label="Phone number"
                  placeholder="024 444 4444"
                  required
                  error={touched.phone ? errors.phone : undefined}
                  helperText={touched?.phone ? errors.phone : undefined}
                  className="rounded"
                />
              </div>

              <div className="flex gap-x-8 justify-between">
                {/* <SelectDropdown
                  label="Gender"
                  name="gender"
                  listSelectedValue={values.gender}
                  list={gender?.map((gender: IEnum) => ({
                    name: gender.title,
                    value: gender.key,
                    description: gender.description
                  }))}
                  onChange={(val: string | null) => setFieldValue('gender', val)}
                  onBlur={handleBlur}
                  placeholder="Select gender"
                  error={touched.gender ? errors.gender : undefined}
                  helperText={touched.gender ? errors.gender : undefined}
                  required
                  wrapperClasses="w-full"
                /> */}
                <SelectDropdown
                  label="Role"
                  name="accessRight"
                  listSelectedValue={values.accessRight}
                  list={accessRights}
                  onChange={(val: string | null) => setFieldValue('accessRight', val)}
                  onBlur={handleBlur}
                  placeholder="Select access right"
                  error={touched.accessRight ? errors.accessRight : undefined}
                  helperText={touched.accessRight ? errors.accessRight : undefined}
                  required
                  wrapperClasses="w-full"
                />
                 <Input
                  name="jobTitle"
                  label="Job Title"
                  value={values.jobTitle}
                  type="text"
                  wrapperClasses=""
                  required
                  error={errors.jobTitle}
                  onChange={handleChange}
                  helperText={errors.jobTitle}
                  className=""
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-5">
               

                {user.isInternalOwner() &&
                  <SelectDropdown
                    name="slackWorkspaceId"
                    label="Slack Workspace"
                    placeholder="Select workspace "
                    listSelectedValue={values.slackWorkspaceId}
                    onChange={(value: any) => {
                      setFieldValue('slackWorkspaceId', value);
                    }}
                    list={workspaces?.map((workspace: any) => ({
                      name: workspace?.name,
                      value: workspace?.id
                    }))}
                  />}
              </div>

              <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={onClose}
                  bgColor='secondary'
                  fullWidth
                  // loading={submitting}
                  disabled={submitting}
                />
                <Button
                  type="submit"
                  label="Submit"
                  bgColor='primary'
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default InviteTeamMember;
